import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./BookEpisodeReader.module.css";
import { ArrowLeft, Settings, ChevronLeft, ChevronRight } from "lucide-react";
import { useEbookBySlug } from "../../hooks/useEbooksHooks";
import { Helmet } from "react-helmet";

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

const EbookReader = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { ebook, isLoading, error } = useEbookBySlug(slug);
  const isMobile = useIsMobile();

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fontSize, setFontSize] = useState(isMobile ? 14 : 18);
  const [pageColor, setPageColor] = useState("#f8f5e9"); // Default cream color
  const [textColor, setTextColor] = useState("#333333"); // Default dark gray
  const [showSettings, setShowSettings] = useState(false);
  const [pages, setPages] = useState([]);
  const [showPageJump, setShowPageJump] = useState(false);
  const [jumpPageNumber, setJumpPageNumber] = useState("");

  const contentRef = useRef(null);

  // Colors for theme options
  const colorThemes = [
    { bg: "#f8f5e9", text: "#333333", name: "Cream" },
    { bg: "#ffffff", text: "#333333", name: "White" },
    { bg: "#f0f0f0", text: "#333333", name: "Light Gray" },
    { bg: "#1a1a1a", text: "#e0e0e0", name: "Dark" },
    { bg: "#2d2d2d", text: "#d4c7a5", name: "Sepia Dark" },
  ];

  // Update fontSize when device changes
  useEffect(() => {
    setFontSize(isMobile ? 14 : 18);
  }, [isMobile]);

  // Calculate pages based on content and container size
useEffect(() => {
  if (isLoading || error || !ebook?.full_content) return;

  // Use different words per page based on device
  const averageWordsPerPage = isMobile ? 200 : 250;

  // Parse HTML content properly
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = ebook.full_content;

  // Function to count words in text content
  const countWords = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  // Function to split content while preserving HTML structure
  const splitContentIntoPages = (htmlContent, wordsPerPage) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${htmlContent}</div>`, 'text/html');
    const container = doc.querySelector('div');
    
    const pages = [];
    let currentPage = document.createElement('div');
    let currentWordCount = 0;
    
    // Function to split large text content into smaller chunks while preserving word boundaries
    const splitTextIntoChunks = (text, maxWords) => {
      const words = text.trim().split(/\s+/).filter(word => word.length > 0);
      const chunks = [];
      
      for (let i = 0; i < words.length; i += maxWords) {
        chunks.push(words.slice(i, i + maxWords).join(' '));
      }
      
      return chunks;
    };
    
    // Function to create a new page
    const createNewPage = () => {
      if (currentPage.innerHTML.trim()) {
        pages.push({
          htmlContent: currentPage.innerHTML,
          wordCount: currentWordCount
        });
      }
      currentPage = document.createElement('div');
      currentWordCount = 0;
    };
    
    // Function to process nodes recursively
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (!text) return;
        
        const words = text.split(/\s+/).filter(word => word.length > 0);
        
        for (let i = 0; i < words.length; i++) {
          if (currentWordCount >= wordsPerPage && currentPage.innerHTML.trim()) {
            createNewPage();
          }
          
          // Add word to current page
          if (currentPage.lastChild && currentPage.lastChild.nodeType === Node.TEXT_NODE) {
            currentPage.lastChild.textContent += ' ' + words[i];
          } else {
            currentPage.appendChild(document.createTextNode(words[i]));
          }
          currentWordCount++;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const blockElements = ['TABLE', 'TR', 'TD', 'TH', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI'];
        const splittableElements = ['P']; // Elements that can be split across pages
        
        if (blockElements.includes(node.tagName) && !splittableElements.includes(node.tagName)) {
          // Handle non-splittable block elements (tables, headers, lists, etc.)
          const elementWordCount = countWords(node.textContent);
          
          if (currentWordCount + elementWordCount > wordsPerPage && currentPage.innerHTML.trim()) {
            createNewPage();
          }
          
          const clonedElement = node.cloneNode(true);
          currentPage.appendChild(clonedElement);
          currentWordCount += elementWordCount;
          
        } else if (splittableElements.includes(node.tagName)) {
          // Handle splittable elements like paragraphs
          const elementWordCount = countWords(node.textContent);
          
          // If the paragraph is small enough to fit on current page or start a new page
          if (elementWordCount <= wordsPerPage) {
            // Check if it fits on current page
            if (currentWordCount + elementWordCount > wordsPerPage && currentPage.innerHTML.trim()) {
              createNewPage();
            }
            
            const clonedElement = node.cloneNode(true);
            currentPage.appendChild(clonedElement);
            currentWordCount += elementWordCount;
          } else {
            // Split large paragraph across multiple pages
            const textContent = node.textContent;
            const remainingWordsForCurrentPage = wordsPerPage - currentWordCount;
            
            // If current page has content and can't fit much more, start fresh
            if (currentWordCount > 0 && remainingWordsForCurrentPage < wordsPerPage * 0.3) {
              createNewPage();
            }
            
            const availableWordsForCurrentPage = wordsPerPage - currentWordCount;
            const textChunks = splitTextIntoChunks(textContent, availableWordsForCurrentPage > 0 ? availableWordsForCurrentPage : wordsPerPage);
            
            textChunks.forEach((chunk, index) => {
              if (index > 0 || (currentWordCount > 0 && index === 0)) {
                createNewPage();
              }
              
              // Create a new paragraph element with the chunk
              const pElement = document.createElement(node.tagName);
              
              // Copy attributes from original element
              Array.from(node.attributes).forEach(attr => {
                pElement.setAttribute(attr.name, attr.value);
              });
              
              // Handle mixed content (text + inline elements)
              if (node.children.length > 0) {
                // If paragraph contains inline elements, we need to preserve them
                // For simplicity, we'll clone the structure and replace text content
                const clonedElement = node.cloneNode(true);
                clonedElement.textContent = chunk;
                pElement.appendChild(clonedElement.firstChild || document.createTextNode(chunk));
              } else {
                // Simple text content
                pElement.textContent = chunk;
              }
              
              currentPage.appendChild(pElement);
              currentWordCount += countWords(chunk);
            });
          }
        } else {
          // Handle inline elements
          const clonedElement = node.cloneNode(false);
          const originalParent = currentPage;
          currentPage.appendChild(clonedElement);
          
          const tempCurrentPage = currentPage;
          currentPage = clonedElement;
          
          Array.from(node.childNodes).forEach(child => processNode(child));
          
          currentPage = tempCurrentPage;
        }
      }
    };
    
    // Process all child nodes
    Array.from(container.childNodes).forEach(child => processNode(child));
    
    // Add the last page if it has content
    if (currentPage.innerHTML.trim()) {
      pages.push({
        htmlContent: currentPage.innerHTML,
        wordCount: currentWordCount
      });
    }
    
    return pages;
  };

  try {
    const contentPages = splitContentIntoPages(ebook.full_content, averageWordsPerPage);
    
    // If no pages were created or splitting failed, fall back to single page
    if (contentPages.length === 0) {
      contentPages.push({
        htmlContent: ebook.full_content,
        wordCount: countWords(tempDiv.textContent)
      });
    }
    
    setPages(contentPages);
    setTotalPages(contentPages.length);
  } catch (error) {
    console.error('Error splitting content into pages:', error);
    // Fallback to single page
    setPages([{
      htmlContent: ebook.full_content,
      wordCount: countWords(tempDiv.textContent)
    }]);
    setTotalPages(1);
  }
}, [ebook, isLoading, error, isMobile]);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const applyColorTheme = (bg, text) => {
    setPageColor(bg);
    setTextColor(text);
  };

  const changeFontSize = (delta) => {
    const newSize = fontSize + delta;
    if (newSize >= 14 && newSize <= 24) {
      setFontSize(newSize);
    }
  };

  const handlePageJump = () => {
    const pageNum = parseInt(jumpPageNumber);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum - 1);
      setJumpPageNumber("");
      setShowPageJump(false);
      window.scrollTo(0, 0);
    }
  };

  const handlePageJumpInputChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setJumpPageNumber(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      handleNextPage();
    } else if (e.key === "ArrowLeft") {
      handlePrevPage();
    } else if (e.key === "Escape") {
      setShowSettings(false);
      setShowPageJump(false);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentPage, totalPages]);

  if (isLoading) {
    return (
      <div className={styles.loaderContainer}>
        <div className={styles.loader}></div>
        <p>Loading ebook...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <h2>Error loading ebook</h2>
        <p>{error.message || "Something went wrong"}</p>
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          <ArrowLeft size={20} /> Go Back
        </button>
      </div>
    );
  }

  if (!ebook) {
    return (
      <div className={styles.errorContainer}>
        <h2>Ebook not found</h2>
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          <ArrowLeft size={20} /> Go Back
        </button>
      </div>
    );
  }

  return (
    <>
    <Helmet>
      <title>{ebook.title} - Ebook Reader</title>
      <meta name="description" content={`Read ${ebook.title} by ${ebook.author || ebook.creator_name} on SunoKahaniyan.`} />
    </Helmet>
    <div
      className={styles.readerContainer}
      style={{ backgroundColor: pageColor }}
    >
      <div
        className={styles.header}
        style={{ backgroundColor: pageColor, color: textColor }}
      >
        <button
          onClick={() => navigate(-1)}
          className={styles.backButton}
          style={{ color: textColor }}
        >
          <ArrowLeft size={20} />
        </button>
        <div className={styles.bookInfo} style={{ color: textColor }}>
          <h1 style={{ color: textColor }}>{ebook.title}</h1>
          <p style={{ color: textColor, opacity: 0.7 }}>
            by {ebook.author || ebook.creator_name}
          </p>
        </div>
        <button
          onClick={toggleSettings}
          className={styles.settingsButton}
          style={{ color: textColor }}
        >
          <Settings size={20} />
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div
          className={styles.settingsPanel}
          style={{ backgroundColor: pageColor, color: textColor }}
        >
          {/* Close Button */}
          <button
            className={styles.closeButton}
            onClick={() => setShowSettings(false)}
            style={{ color: textColor }}
          >
            ×
          </button>

          <h2 className={styles.settingsTitle} style={{ color: textColor }}>
            Themes & Settings
          </h2>

          {/* Font Size Section */}
          <div className={styles.settingsSection}>
            <div className={styles.fontSizeControls}>
              <button
                onClick={() => changeFontSize(-1)}
                disabled={fontSize <= 14}
                className={styles.fontSizeButton}
                style={{
                  borderColor: textColor,
                  color: textColor,
                  backgroundColor: "transparent",
                }}
              >
                A
              </button>
              <div
                className={styles.fontSizeDivider}
                style={{ backgroundColor: textColor }}
              ></div>
              <button
                onClick={() => changeFontSize(1)}
                disabled={fontSize >= 24}
                className={styles.fontSizeButton}
                style={{
                  borderColor: textColor,
                  color: textColor,
                  backgroundColor: "transparent",
                }}
              >
                A
              </button>
            </div>
          </div>

          {/* Theme Options */}
          <div className={styles.settingsSection}>
            <div className={styles.themeGrid}>
              {colorThemes.map((theme, index) => (
                <button
                  key={index}
                  className={`${styles.themeCard} ${
                    theme.bg === pageColor && theme.text === textColor
                      ? styles.activeTheme
                      : ""
                  }`}
                  style={{
                    backgroundColor: theme.bg,
                    border:
                      theme.bg === pageColor && theme.text === textColor
                        ? `2px solid ${textColor}`
                        : `1px solid ${theme.text}20`,
                  }}
                  onClick={() => applyColorTheme(theme.bg, theme.text)}
                >
                  <div className={styles.themePreview}>
                    <span
                      className={styles.themeText}
                      style={{ color: theme.text }}
                    >
                      Aa
                    </span>
                  </div>
                  <span
                    className={styles.themeName}
                    style={{ color: theme.text }}
                  >
                    {theme.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Page Jump Modal */}
      {showPageJump && (
        <div className={styles.pageJumpOverlay}>
          <div
            className={styles.pageJumpModal}
            style={{ backgroundColor: pageColor, color: textColor }}
          >
            <h3 style={{ color: textColor }}>Go to Page</h3>
            <div className={styles.pageJumpControls}>
              <input
                type="text"
                value={jumpPageNumber}
                onChange={handlePageJumpInputChange}
                placeholder={`1-${totalPages}`}
                className={styles.pageJumpInput}
                style={{
                  backgroundColor: "transparent",
                  color: textColor,
                  borderColor: textColor,
                }}
                onKeyPress={(e) => e.key === "Enter" && handlePageJump()}
                autoFocus
              />
              <button
                onClick={handlePageJump}
                className={styles.pageJumpButton}
                style={{
                  backgroundColor: textColor,
                  color: pageColor,
                }}
                disabled={
                  !jumpPageNumber ||
                  parseInt(jumpPageNumber) < 1 ||
                  parseInt(jumpPageNumber) > totalPages
                }
              >
                Go
              </button>
            </div>
            <button
              onClick={() => setShowPageJump(false)}
              className={styles.pageJumpCancel}
              style={{ color: textColor }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Content Area */}
     <div
  className={styles.contentArea}
  ref={contentRef}
  style={{
    fontSize: `${fontSize}px`,
    color: textColor,
    backgroundColor: pageColor,
  }}
>
  {pages.length > 0 ? (
    <div className={styles.pageContent}>
      {currentPage === 0 && (
        <div className={styles.bookHeader}>
          <h1 className={styles.bookTitle} style={{ color: textColor }}>
            {ebook.title}
          </h1>
          <h2
            className={styles.bookAuthor}
            style={{ color: textColor, opacity: 0.8 }}
          >
            by {ebook.author || ebook.creator_name}
          </h2>
          <div
            className={styles.titleSeparator}
            style={{ backgroundColor: textColor, opacity: 0.3 }}
          ></div>
        </div>
      )}
      {/* Add CSS for proper table rendering */}
      <style jsx>{`
        .pageContent table {
          border-collapse: collapse;
          width: 100%;
          margin: 1rem 0;
          border: 1px solid ${textColor}40;
        }
        .pageContent td, .pageContent th {
          border: 1px solid ${textColor}40;
          padding: 8px 12px;
          text-align: left;
        }
        .pageContent th {
          background-color: ${textColor}10;
          font-weight: bold;
        }
        .pageContent p {
          margin: 0.5rem 0;
          line-height: 1.6;
        }
        .pageContent em {
          font-style: italic;
        }
        .pageContent strong {
          font-weight: bold;
        }
      `}</style>
      {/* Use dangerouslySetInnerHTML for better HTML rendering */}
      <div
        className="pageContent"
        dangerouslySetInnerHTML={{
          __html: pages[currentPage]?.htmlContent || "",
        }}
      />
    </div>
  ) : (
    <div
      className={styles.pageContent}
      dangerouslySetInnerHTML={{ __html: ebook.full_content }}
    />
  )}
</div>

      {/* Pagination Controls */}
      <div
        className={styles.paginationControls}
        style={{ backgroundColor: pageColor, borderColor: `${textColor}20` }}
      >
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 0}
          className={styles.pageButton}
          style={{ color: textColor }}
          aria-label="Previous page"
        >
          <ChevronLeft size={24} />
        </button>
        <div className={styles.pageInfo} style={{ color: textColor }}>
          <button
            onClick={() => setShowPageJump(true)}
            className={styles.pageInfoButton}
            style={{ color: textColor }}
          >
            Page {currentPage + 1} of {totalPages || 1}
          </button>
        </div>
        <button
          onClick={handleNextPage}
          disabled={currentPage >= totalPages - 1}
          className={styles.pageButton}
          style={{ color: textColor }}
          aria-label="Next page"
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
    </>
  );
};

export default EbookReader;